import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { receiveTickets } from '@ttstr/actions';
import { Container, LoadingSpinner, Masonry, useIntl } from '@ttstr/components';
import { AppState } from '@ttstr/reducers';
import { useActions, useShallowEqualSelector } from '@ttstr/utils';
import { Article } from '@ttstr/api';

type Props = {
  /**
   * ticketFilter can be used if you want a Route to a dedicated page for special tickets.
   *
   * Example:
   * You want a page only with tickets that has the tag id 1234.
   * The function for ticketFilter would then be
   *  p => p.tag_ids.includes(1234)
   *
   * Then create a route in App.tsx with
   * <Route exact path="/specialtickets" render={()=> <TicketView ticketFilter={p => p.tag_ids.includes(1234)} />} />
   */
  ticketFilter?: (p: Article) => any;
  showHeading?: boolean;
  showLocation?: boolean;
  showCity?: boolean;
  showTitle?: boolean;
  showSupertitle?: boolean;
  showSubtitle?: boolean;
  showBrand?: boolean;
  showCategories?: boolean;
  showTags?: boolean;
};

const TicketsView: React.FC<Props> = ({
  ticketFilter = (t) => t,
  showHeading = false,
  showLocation = false,
  showCity = false,
  showTitle = false,
  showSupertitle = false,
  showSubtitle = false,
  showBrand = false,
  showCategories = false,
  showTags = false,
}) => {
  const { t } = useTranslation();
  const { loading, tickets } = useShallowEqualSelector(mapStateToProps);
  const { receiveTickets } = useActions(mapDispatchToProps);
  const { language } = useIntl();

  React.useEffect(() => {
    receiveTickets();
  }, [language]);

  return (
    <article className="py-5 ticket-listing">
      <Container className="list-page">
        <Helmet>
          <title>{t(`TICKETLISTING.TITLE`)}</title>
        </Helmet>

        {showHeading && <h1 className="text-center mb-4">{t(`TICKETLISTING.TITLE`)}</h1>}

        <Masonry
          id="ticket-listing"
          products={ticketFilter ? tickets.filter(ticketFilter) : tickets}
          type="list"
          filters="off"
          showLocation={showLocation}
          showCity={showCity}
          showTitle={showTitle}
          showSupertitle={showSupertitle}
          showSubtitle={showSubtitle}
          showBrand={showBrand}
          showCategories={showCategories}
          showTags={showTags}
        />

        {loading && <LoadingSpinner label={t(`LOADING.TICKETS`)} />}
      </Container>
    </article>
  );
};

const mapStateToProps = (state: AppState) => {
  const { tickets, loading } = state.Tickets;
  return {
    tickets,
    loading,
  };
};

const mapDispatchToProps = {
  receiveTickets,
};

export default React.memo(TicketsView);
